import React, { Fragment, useMemo, useState, useRef } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import { Text, Icon, Flex, Block } from '@upsales/components';
import './SubMenuItem.scss';
import NavbarSubMenuDropdown from '../NavbarSubMenuDropdown';
import { useTranslation } from 'Components/Helpers/translate';
import DropdownItem from 'App/components/DropdownItem';
import { globalTracker } from 'App/babel/helpers/Tracker';
import { useSelector } from 'App/components/hooks';

type StateParams = {
	entity?: string;
	[key: string]: any;
};

type SubItem = {
	id?: string | number;
	title: string;
	hidden?: boolean;
	state?: string;
	stateParams?: StateParams;
};

type Section = {
	type?: 'header' | (string & {});
	title: string;
	items?: SubItem[] | null;
	state?: string;
	stateParams?: StateParams;
};

type Item = {
	id: string;
	title: string;
	dropdown?: Section[] | null;
	key?: string;
	state: string;
	stateParams?: object;
	activeStatePrefix?: string;
	/** Never show as active while the current state starts with this prefix —
	 *  lets a broad activeStatePrefix carve out a sibling tab's sub-states. */
	excludeStatePrefix?: string;
	/** Optional leading icon (e.g. 'cog' for settings). */
	icon?: React.ComponentProps<typeof Icon>['name'];
	/** Push this item (and any following) to the right edge of the submenu. */
	pushRight?: boolean;
};

type Props = {
	item: Item;
	className?: string;
	goToState: (state: string, params?: object) => void;
	alignRight?: boolean;
};

const getHref = (stateName: string, params: Record<string, any>) => {
	if (stateName.startsWith('/')) {
		return stateName;
	}
	return Tools.$state.href(stateName, params);
};

const onClick = (item: SubItem, section: Section, customerId: number) => {
	globalTracker.track(`navbar.dropdown.${item.state || section.state}`);

	let link;
	if (item.state) {
		link = getHref(item.state, {
			entity: item.stateParams?.entity,
			customerId
		});
	} else if (item.stateParams) {
		link = getHref(section.state!, {
			...(item.stateParams || {}),
			customerId
		});
	} else {
		link =
			getHref(section.state!, {
				...(section.stateParams || {}),
				customerId
			}) +
			'?id=' +
			item.id;
	}
	window.location.href = link;
};

type RouteMapping = typeof ROUTE_MAPPINGS;

const ROUTE_MAPPINGS = {
	'react-root-contacts': 'react-root-companies',
	'react-root-projects': 'react-root-projectBoard',
	'react-root-projectMap': 'react-root-projectBoard',
	appointments: 'activities',
	'react-root-landingpages': 'react-root-forms',
	'react-root-flows': 'react-root-segments',
	'react-root-marketingboard': 'react-root-leads',
	'react-root-calllists': 'react-root-campaigns',
	'react-root-opportunities': 'react-root-salesboard',
	periodizations: 'react-root-salesboard'
};

const SubMenuItem = ({ item, className, goToState, alignRight = false }: Props) => {
	const { title, dropdown, state, stateParams, activeStatePrefix, excludeStatePrefix, icon, pushRight } = item;
	const classes = new BemClass('SubMenuItem', className);
	const [showDropdown, setShowDropdown] = useState(false);
	const dropdownArrowRef = useRef<HTMLDivElement>(null);

	const { t } = useTranslation();
	const currentState = Tools.$state.current.name as string;
	const active =
		(currentState === state ||
			ROUTE_MAPPINGS[currentState as keyof RouteMapping] === state ||
			(!!activeStatePrefix && currentState.startsWith(activeStatePrefix))) &&
		!(excludeStatePrefix && currentState.startsWith(excludeStatePrefix));
	const { customerId } = useSelector(({ App }) => App);
	const dropdownContent = useMemo(
		() =>
			dropdown?.map((section, index) => {
				if (section.type === 'header') {
					return (
						<Text key={section.title + index} size="xl" bold space="ptm pbm plm prm">
							{section.title}
						</Text>
					);
				}
				return section.items?.length ? (
					<Fragment key={section.state! + index}>
						<Block space="ptm pbm plm prm">
							<Text bold>{section.title}</Text>
						</Block>

						{section.items.map((item, index) =>
							!item.hidden ? (
								<DropdownItem
									key={item.title + index}
									title={t(item.title)}
									onClick={e => {
										e.stopPropagation(); // Prevent click event from bubbling up to the parent and triggering the item click
										onClick(item, section, customerId);
										setShowDropdown(false);
									}}
								/>
							) : null
						)}
					</Fragment>
				) : null;
			}),
		[dropdown]
	);

	return (
		<Flex
			className={classes.mod({ active, 'has-dropdown': !!dropdown?.length, 'push-right': !!pushRight }).b()}
			onClick={() => goToState(state, stateParams)}
			alignItems="center"
			gap="u1"
		>
			{icon ? <Icon name={icon} className={classes.elem('icon').b()} /> : null}
			<Text className={classes.elem('title').b()} bold>
				{title}
			</Text>
			{dropdown?.length ? (
				<>
					<Flex
						className={classes.elem('drop-trigger').mod({ active: showDropdown }).b()}
						alignItems="center"
						justifyContent="center"
						onClick={e => {
							e.stopPropagation();
							setShowDropdown(!showDropdown);
						}}
						ref={dropdownArrowRef}
					>
						<Icon name="chevron-down" />
					</Flex>
					<NavbarSubMenuDropdown
						isOpen={showDropdown}
						onOpenChange={open => setShowDropdown(open)}
						align={alignRight ? 'right' : 'left'}
						insideRefs={[dropdownArrowRef.current]}
					>
						{dropdownContent}
					</NavbarSubMenuDropdown>
				</>
			) : null}
		</Flex>
	);
};

export default SubMenuItem;
